import { useState, useCallback, useEffect } from "react";
import useInspectionAttributes from "../../../hooks/query/useInspectionAttributes";
import { hydrateMeasurementsFromAttributes } from "../utils/measurements";
import { offlineStore, OfflineInspection } from "../../../utils/offlineStore";

export interface BackendAttribute {
    Id: string;
    AttributeName: string;
    AttributeValue: any;
    Qty?: number;
    Action?: string;
    CalculatedValue?: number;
}

export const useInit = ({ 
    projectId, 
    onSuccess 
}: { 
    projectId?: string; 
    onSuccess?: (data: Record<string, any>) => void;
} = {}) => {
    const [loading, setLoading] = useState(!!projectId);
    const [error, setError] = useState<string | null>(null);
    const [baselineAttributes, setBaselineAttributes] = useState<BackendAttribute[]>([]);

    const { useListByProjectId } = useInspectionAttributes();
    const { mutateAsync: listAttributes } = useListByProjectId();

    const loadInitialData = useCallback(async (projectId: string) => {
        setLoading(true);
        setError(null);
        try {
            console.log("Loading initial data for project:", projectId);
            
            const mappedId = await offlineStore.getProjectMapping(projectId);
            const realProjectId = mappedId || projectId;
            
            if (realProjectId.startsWith("offline-")) {
                const inspections = await offlineStore.getInspections();
                const offlineData = inspections.find((i: OfflineInspection) => i.offlineId === projectId);
                if (offlineData) {
                    return { success: true, flatData: offlineData.payload, baselineAttributes: [] };
                }
                return { success: true, flatData: {}, baselineAttributes: [] };
            }

            const response = await listAttributes({ ProjectId: realProjectId });
            
            if (response && Array.isArray(response)) {
                setBaselineAttributes(response);

                const { flatData } = hydrateMeasurementsFromAttributes(response);

                return { success: true, flatData, baselineAttributes: response };
            }
            
            return { success: true, flatData: {}, baselineAttributes: [] };
        } catch (err: any) {
            console.error("Failed to load initial data:", err);
            setError(err.message || "Failed to load data");
            return { success: false, error: err };
        } finally {
            setLoading(false);
        }
    }, [listAttributes]);

    // Automatically load data when projectId is provided
    useEffect(() => {
        if (projectId) {
            loadInitialData(projectId).then((res) => {
                if (res.success && res.flatData && onSuccess) {
                    onSuccess(res.flatData);
                }
            });
        }
    }, [projectId]);

    return {
        loadInitialData,
        baselineAttributes,
        setBaselineAttributes,
        loading,
        error,
    };
};
